import { makeAutoObservable, runInAction } from 'mobx';
import { logger } from '@/utils/logger';

/**
 * 用户信息管理 Store
 * 负责统一管理用户信息的获取和缓存
 */
class UserStore {
  // 用户信息映射表
  _users = new Map();

  // 加载状态
  _isLoading = false;

  // 错误信息
  _error = null;

  // 添加重试机制：记录API返回空结果的用户ID
  _failedUserIds = new Set();

  // 添加重试次数限制
  _retryCount = 0;
  _maxRetries = 3;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * 获取用户信息映射表
   */
  get users() {
    return this._users;
  }

  /**
   * 获取加载状态
   */
  get isLoading() {
    return this._isLoading;
  }

  /**
   * 获取错误信息
   */
  get error() {
    return this._error;
  }

  /**
   * 根据用户ID获取用户信息
   * @param {string|number} userId - 用户ID
   * @returns {Object|null} 用户信息
   */
  getUser(userId) {
    return this._users.get(userId?.toString()) || null;
  }

  /**
   * 清除指定用户ID的缓存
   * @param {Array<string|number>} userIds - 要清除的用户ID数组
   */
  clearUsers(userIds) {
    if (!userIds || userIds.length === 0) {
      return;
    }

    runInAction(() => {
      userIds.forEach(userId => {
        this._users.delete(userId?.toString());
      });
    });

    logger.debug(`[UserStore] 清除 ${userIds.length} 个用户的缓存`);
  }

  /**
   * 清除所有用户缓存
   */
  clearAllUsers() {
    runInAction(() => {
      this._users.clear();
      this._failedUserIds.clear(); // 同时清理失败列表
    });

    logger.debug('[UserStore] 清除所有用户缓存和失败列表');
  }

  /**
   * 清理失败列表（用于重置重试状态）
   */
  clearFailedUserIds() {
    runInAction(() => {
      this._failedUserIds.clear();
    });
    logger.debug('[UserStore] 清理失败用户ID列表');
  }

  /**
   * 获取所有会话ID
   * @returns {Array<string>} 会话ID数组
   */
  getAllConversationIds() {
    const conversationIds = [];
    this._users.forEach(user => {
      if (user.conversationId) {
        conversationIds.push(user.conversationId);
      }
    });
    return conversationIds;
  }

  /**
   * 获取所有用户信息
   * @returns {Array<Object>} 用户信息数组
   */
  getAllUsers() {
    return Array.from(this._users.values());
  }

  /**
   * 调试方法：检查缓存状态
   * @param {Array<string|number>} userIds - 要检查的用户ID数组
   */
  debugCacheStatus(userIds) {
    logger.debug('[UserStore] 缓存状态检查:', {
      totalCacheSize: this._users.size,
      requestedUserIds: userIds,
      cachedUserIds: userIds.filter(userId => this._users.has(userId?.toString())),
      uncachedUserIds: userIds.filter(userId => !this._users.has(userId?.toString())),
      allCachedKeys: Array.from(this._users.keys()),
      allCachedValues: Array.from(this._users.values()).map(user => ({
        id: user.id,
        nickName: user.nickName,
        hasAvatar: !!user.avatarUrl,
        conversationId: user.conversationId,
      })),
    });
  }

  /**
   * 根据用户ID数组获取会话ID数组
   * @param {Array<string|number>} userIds - 用户ID数组
   * @returns {Array<string>} 会话ID数组
   */
  getConversationIdsByUserIds(userIds) {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    const conversationIds = [];
    userIds.forEach(userId => {
      const user = this.getUser(userId);
      if (user?.conversationId) {
        conversationIds.push(user.conversationId);
      }
    });

    return conversationIds;
  }

  // 为了向后兼容，保留一些别名方法
  /**
   * @deprecated 使用 getUser 替代
   */
  getUserAvatar(userId) {
    return this.getUser(userId);
  }

  /**
   * @deprecated 使用 users 替代
   */
  get userAvatars() {
    return this.users;
  }
}

export const userStore = new UserStore();
